/**
 * Popular Trims Page
 * Ranked list of most-viewed trims across dealer's inventory.
 * Data: /analytics/top-vehicles (aggregated client-side by year+make+model+trim).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tag, Inbox, AlertCircle, RefreshCw } from "lucide-react";
import { TimeRangeSelector } from "../components/dashboard";
import TableScroll from "../components/TableScroll";

const TrimsSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 animate-pulse">
        <div className="w-7 h-7 rounded-full bg-gray-200" />
        <div className="flex-1 h-4 bg-gray-200 rounded w-3/4" />
        <div className="w-16 h-4 bg-gray-200 rounded" />
      </div>
    ))}
  </div>
);

const PopularTrimsPage = () => {
  const { apiFetch } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawItems, setRawItems] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Pull a large pool from both views+unlocks, then aggregate by trim
      const [viewsRes, unlocksRes] = await Promise.all([
        apiFetch(`/analytics/top-vehicles?days=${days}&metric=views&limit=50`),
        apiFetch(`/analytics/top-vehicles?days=${days}&metric=unlocks&limit=50`),
      ]);
      const merged = new Map();
      const add = (list) => {
        (list || []).forEach(v => {
          const key = [v.year || "", v.make || "", v.model || "", v.trim || ""].join("|");
          if (!v.make && !v.model) return; // skip unknown vehicles
          const prev = merged.get(key) || {
            year: v.year, make: v.make, model: v.model, trim: v.trim,
            views_count: 0, unlocks_count: 0,
          };
          prev.views_count = Math.max(prev.views_count, v.views_count || 0);
          prev.unlocks_count = Math.max(prev.unlocks_count, v.unlocks_count || 0);
          merged.set(key, prev);
        });
      };
      add(viewsRes.data?.items);
      add(unlocksRes.data?.items);
      setRawItems(Array.from(merged.values()));
    } catch (err) {
      console.error("Failed to fetch popular trims:", err);
      setError(err.response?.data?.detail || "Failed to load trim data");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ranked = useMemo(() => {
    // Aggregate by Make+Model+Trim (ignoring year differences to get popular trim across years)
    const bucket = new Map();
    rawItems.forEach(item => {
      const trimKey = [item.make || "", item.model || "", item.trim || ""].join("|");
      const cur = bucket.get(trimKey) || {
        make: item.make, model: item.model, trim: item.trim,
        views_count: 0, unlocks_count: 0,
      };
      cur.views_count += item.views_count || 0;
      cur.unlocks_count += item.unlocks_count || 0;
      bucket.set(trimKey, cur);
    });
    return Array.from(bucket.values()).sort((a, b) => b.views_count - a.views_count);
  }, [rawItems]);

  if (error && !loading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-red-50 rounded-xl">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="text-lg font-medium text-red-600">Failed to load trim data</p>
          <p className="text-sm text-red-500 mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
            data-testid="popular-trims-retry"
          >
            <RefreshCw className="w-4 h-4" /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full" data-testid="popular-trims-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Popular Trims</h1>
          <p className="text-gray-500 text-sm mt-0.5">Most-viewed trims across your inventory</p>
        </div>
        <TimeRangeSelector value={days} onChange={setDays} disabled={loading} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-500" />
              <CardTitle className="text-lg">Trim Rankings</CardTitle>
            </div>
            <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
              Last {days} days
            </span>
          </div>
          <CardDescription>Aggregated by Make / Model / Trim</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <TrimsSkeleton />
          ) : ranked.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Inbox className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-base font-medium text-gray-500 mb-1">No trim data yet</p>
              <p className="text-sm text-gray-400 max-w-xs">Once visitors view vehicles on your site, trim rankings will appear here.</p>
            </div>
          ) : (
            <TableScroll testId="popular-trims-scroll">
              <table className="w-full text-sm" data-testid="popular-trims-table">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase text-gray-500 tracking-wide">
                    <th className="py-3 px-3 w-16">Rank</th>
                    <th className="py-3 px-3">Trim Name</th>
                    <th className="py-3 px-3">Make / Model</th>
                    <th className="py-3 px-3 text-right">Views</th>
                    <th className="py-3 px-3 text-right">Unlocks</th>
                    {/* iter91 — Issue 5: Unlock Rate is the lowest-priority
                        column (derived from Views/Unlocks). Hidden at
                        <lg viewports; on hover the row's rate appears
                        in a title attribute on the row. */}
                    <th className="py-3 px-3 text-right hide-lg">Unlock Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((row, idx) => {
                    const rate = row.views_count > 0 ? ((row.unlocks_count / row.views_count) * 100) : 0;
                    return (
                      <tr
                        key={`${row.make}-${row.model}-${row.trim}-${idx}`}
                        className="border-b border-gray-100 hover:bg-gray-50"
                        data-testid={`popular-trim-row-${idx}`}
                        title={`Unlock rate: ${rate.toFixed(1)}%`}
                      >
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${idx < 3 ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="py-3 px-3 font-medium text-gray-900">{row.trim || <span className="text-gray-400 italic">Base trim</span>}</td>
                        <td className="py-3 px-3 text-gray-600">{row.make} {row.model}</td>
                        <td className="py-3 px-3 text-right font-semibold tabular-nums">{row.views_count.toLocaleString()}</td>
                        <td className="py-3 px-3 text-right tabular-nums text-gray-700">{row.unlocks_count.toLocaleString()}</td>
                        <td className="py-3 px-3 text-right tabular-nums hide-lg">
                          <span className={`font-semibold ${rate > 10 ? 'text-emerald-600' : rate > 5 ? 'text-amber-600' : 'text-gray-500'}`}>
                            {rate.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PopularTrimsPage;
